import { action, observable, runInAction } from "mobx";
import { BaseEditViewStore, BaasicDropdownStore, StepperStore } from "core/stores";
import { NikoTemplateCreateViewStore } from "application/niko-patient/stores";
import { NikoPatientService, NikoPatientTemplateService, NikoPatientDetailService } from "application/niko-patient/services";
import { InsuranceService } from "application/import-reference/services";
import { LookupService } from "common/services";
import { ModalParams } from "core/models";
import { NikoCreateNewPatientForm } from "application/niko-patient/forms";
import { PatientTemplateCreateForm } from "application/niko-patient/forms";
import { uuid } from "core/utils";
import { EligibilityStatusService } from "../../eligibility-status/services";
import { EligibilityStatusName } from "../constants";
import _ from "lodash";

class NikoCreateNewPatientModalStore extends BaseEditViewStore {
    @observable accessor primaryPolicies = [];
    @observable accessor secondaryPolicies = [];
    @observable accessor removedPolicies = [];
    @observable accessor templateInstances = [];
    @observable accessor disabled = false;
    @observable accessor ssnError = null;
    @observable accessor policyErrors = {};

    // Existing patient check
    @observable accessor existingPatients = [];
    @observable accessor selectedExistingPatient = null;
    @observable accessor scenario = null;
    @observable accessor existingNikoPatientId = null;
    @observable accessor activeNikoPatientDetailId = null;
    @observable accessor showExistingPatientList = false;
    @observable accessor checkingExisting = false;
    @observable accessor resolvingScenario = false;
    @observable accessor blockingMessage = null;

    // Insurance preloading
    @observable accessor existingInsuranceData = null;
    @observable accessor insurancePreloaded = false;

    // PAR popup
    parConfirmModal = new ModalParams({});
    insuranceQuestionModal = new ModalParams({});
    insuranceChangesNoteModal = new ModalParams({});
    insuranceInfoNoteModal = new ModalParams({});
    insuranceStatusNoteModal = new ModalParams({});
    eligibilityStatusNoteModal = new ModalParams({});
    deletePolicyModal = new ModalParams({});
    inactivatePolicyModal = new ModalParams({});
    @observable accessor templatesNeedingPar = [];

    constructor(rootStore, componentProps) {
        super(rootStore, {
            name: "niko-create-new-patient-modal",
            Form: NikoCreateNewPatientForm,
            autoInit: false,
            actions: {},
            FormClass: NikoCreateNewPatientForm,
        });

        this.rootStore = rootStore;
        this.modalParams = componentProps.modalParams;
        this.facilityOrderId = this.modalParams.data.facilityOrderId;
        this.facilityOrder = this.modalParams.data.facilityOrder;
        this.facility = this.modalParams.data.facilityOrder.facility;
        this.facilityId = this.modalParams.data.facilityId || (this.facilityOrder ? this.facilityOrder.facilityId : null);
        this.fetchPatientTemplates = this.modalParams.data.fetchPatientTemplates;
        this.pageName = this.modalParams.data.pageName;
        this.pageType = this.modalParams.data.pageType || "CR";

        this.eligibilityStatusService = new EligibilityStatusService(rootStore.application.baasic.apiClient);
        this.nikoPatientService = new NikoPatientService(rootStore.application.baasic.apiClient);
        this.nikoPatientDetailService = new NikoPatientDetailService(rootStore.application.baasic.apiClient);
        this.insuranceService = new InsuranceService(rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
        this.patientTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);

        this.genderStore = new BaasicDropdownStore(
            { textField: "name", dataItemKey: "name", filterable: false },
            {
                fetchFunc: () => {
                    return [{ id: 1, name: "Male" }, { id: 2, name: "Female" }];
                },
            }
        );

        this.insuranceStatusStore = new BaasicDropdownStore({
            filterable: true,
            }, {
            onChange: (newId) => this.onEligibilityStatusChange(newId),
            fetchFunc: async (term) => {
                const response = await this.eligibilityStatusService.find({
                    searchQuery: term,
                    rpp: 50
                });
                return response.data.item;
            }
        });

        // Recompute the ES Note while the user types the Insurance Info Note, but trailing the
        // keystrokes so we make one backend call after they pause rather than one per character.
        this._lastInsuranceInfoNote = "";
        this.debouncedInfoNoteRecompute = _.debounce(this.onInsuranceInfoNoteChange, 400);

        this.initializeStepper();
        this.addTemplate(); // Start with one template
        this.fetch([this.initialize()]);
    }

    destroy() {
        this.debouncedInfoNoteRecompute.cancel();
        super.destroy();
    }

    get isInsuranceStepSkipped() {
        return this.pageType === "II";
    }

    // True once "Send Order" has been clicked at the FO level. After this point the
    // "Delete" action is never available on an insurance policy (only "Inactivate").
    get isSendOrderActive() {
        return !!(this.facilityOrder && this.facilityOrder.sendOrder);
    }

    get stepperItems() {
        if (this.isInsuranceStepSkipped) {
            return [
                { label: "Patient Info", disabled: false, isValid: undefined, fieldsForValidation: ["firstName", "lastName", "gender", "dateOfBirth"] },
                { label: "Templates", disabled: true, isValid: undefined, fieldsForValidation: [] },
                { label: "Summary", disabled: true, isValid: undefined, fieldsForValidation: [] },
            ];
        }
        return [
            { label: "Patient Info", disabled: false, isValid: undefined, fieldsForValidation: ["firstName", "lastName", "gender", "dateOfBirth"] },
            { label: "Insurance Info", disabled: true, isValid: undefined, fieldsForValidation: [] },
            { label: "Templates", disabled: true, isValid: undefined, fieldsForValidation: [] },
            { label: "Summary", disabled: true, isValid: undefined, fieldsForValidation: [] },
        ];
    }

    // Map currentStep to logical step name
    getStepName(step) {
        if (this.isInsuranceStepSkipped) {
            return ["patientInfo", "templates", "summary"][step];
        }
        return ["patientInfo", "insuranceInfo", "templates", "summary"][step];
    }

    get isPatientInfoStep() {
        return this.getStepName(this.stepperStore.currentStep) === "patientInfo";
    }

    get isInsuranceInfoStep() {
        return this.getStepName(this.stepperStore.currentStep) === "insuranceInfo";
    }

    get isTemplatesStep() {
        return this.getStepName(this.stepperStore.currentStep) === "templates";
    }

    get isSummaryStep() {
        return this.getStepName(this.stepperStore.currentStep) === "summary";
    }

    get isStep1Valid() {
        const f = this.form;
        return f.$("firstName").value && f.$("lastName").value && f.$("gender").value && f.$("dateOfBirth").value && (f.$("ssn").value || f.$("noSsn").value);
    }

    get lastStepIndex() {
        return this.stepperItems.length - 1;
    }

    @action.bound
    initializeStepper() {
        this.stepperStore = new StepperStore(
            {
                showIconIfValid: true,
                form: this.form,
                validatePreviousStep: true,
            },
            this.stepperItems
        );
    }

    @action.bound
    addTemplate() {
        const templateId = uuid();
        const items = observable([]);
        const trachItems = observable([]);
        const form = new PatientTemplateCreateForm({
            onSuccess: () => { },
            onError: () => { },
        });
        const store = new NikoTemplateCreateViewStore(this.rootStore, {
            patientTemplateItems: items,
            patientTemplateTrachItems: trachItems,
            form: form,
            facilityId: this.facilityId,
            facilityOrderId: this.facilityOrderId,
            pageType: this.pageType,
            onSubCategoryChange: (subCategoryValue) => this.checkSystemDns(templateId, subCategoryValue),
        });
        this.templateInstances.push({
            id: templateId,
            store: store,
            items: items,
            trachItems: trachItems,
            form: form,
        });
    }

    @action.bound
    async checkSystemDns(templateId, subCategoryValue) {
        if (!subCategoryValue || !subCategoryValue.itemCategory) return;

        const eligibilityStatusId = this.insuranceStatusStore.value
            ? this.insuranceStatusStore.value.id
            : null;

        const templateInstance = this.templateInstances.find(t => t.id === templateId);
        if (!templateInstance) return;

        try {
            const response = await this.patientTemplateService.checkSystemDns({
                itemCategoryId: subCategoryValue.itemCategory.id,
                eligibilityStatusId: eligibilityStatusId,
                facilityId: this.facilityId,
            });
            const result = response.data || response;
            runInAction(() => {
                templateInstance.store.isSystemDns = result === true;
            });
        } catch (err) {
            this.rootStore.notificationStore.error("Error checking system DNS:", err && err.data);
        }
    }

    @action.bound
    removeTemplate(index) {
        if (this.templateInstances.length > 1) {
            this.templateInstances.splice(index, 1);
        }
    }

    @action
    async initialize() {
        this.form.clear();

        this.form.observe({
            path: "firstName",
            key: "value",
            call: () => this.stepperStore.calculateCurrentStepValidity(),
        });
        this.form.observe({
            path: "lastName",
            key: "value",
            call: () => this.stepperStore.calculateCurrentStepValidity(),
        });
        this.form.observe({
            path: "gender",
            key: "value",
            call: () => this.stepperStore.calculateCurrentStepValidity(),
        });
        this.form.observe({
            path: "dateOfBirth",
            key: "value",
            call: () => this.stepperStore.calculateCurrentStepValidity(),
        });
        this.form.observe({
            path: "noSsn",
            key: "value",
            call: ({ change }) => {
                if (change.newValue) {
                    this.form.$("ssn").set("value", "");
                    this.form.$("ssn").set("disabled", true);
                    this.ssnError = null;
                } else {
                    this.form.$("ssn").set("disabled", false);
                }
            },
        });
    }

    // --- Step 1: Existing Patient Check ---

    @action.bound
    async checkForExistingPatients() {
        this.checkingExisting = true;
        this.blockingMessage = null;
        try {
            const formValues = this.form.values();
            const request = {
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                dateOfBirth: formValues.dateOfBirth,
                ssn: (!formValues.noSsn && formValues.ssn) ? formValues.ssn : null,
            };

            const response = await this.nikoPatientService.checkExisting(request);
            const patients = response.data || response || [];

            runInAction(() => {
                this.checkingExisting = false;
                if (patients.length > 0) {
                    this.existingPatients = patients;
                    this.showExistingPatientList = true;
                } else {
                    this.existingPatients = [];
                    this.showExistingPatientList = false;
                    this.existingNikoPatientId = null;
                    this.activeNikoPatientDetailId = null;
                    this.scenario = null;
                    this.selectedExistingPatient = null;
                }
            });

            return patients.length;
        } catch (err) {
            runInAction(() => {
                this.checkingExisting = false;
            });
            this.rootStore.notificationStore.error("Error checking for existing patients.");
            return -1;
        }
    }

    @action.bound
    async selectExistingPatient(patient) {
        this.selectedExistingPatient = patient;
        this.resolvingScenario = true;

        try {
            const response = await this.nikoPatientService.resolveScenario({
                nikoPatientId: patient.id,
                facilityOrderId: this.facilityOrderId,
                facilityId: this.facilityId,
            });

            const result = response.data || response;

            runInAction(() => {
                this.resolvingScenario = false;

                if (!result.canProceed) {
                    // Scenario 2.1 — blocked
                    this.blockingMessage = result.blockingMessage || "Patient already exists in the Facility Order.";
                    this.selectedExistingPatient = null;
                } else {
                    this.blockingMessage = null;
                    // Scenarios 2.2–2.5 — can proceed
                    this.existingNikoPatientId = patient.id;
                    this.activeNikoPatientDetailId = patient.activeNikoPatientDetailId || null;
                    this.scenario = result.scenario;
                    this.showExistingPatientList = false;
                }
            });

            if (result.canProceed) {
                this.advanceStep();
            }

            return result.canProceed;
        } catch (err) {
            runInAction(() => {
                this.resolvingScenario = false;
                this.selectedExistingPatient = null;
            });
            this.rootStore.notificationStore.error("Error resolving patient scenario.");
            return false;
        }
    }

    @action.bound
    proceedAsNewPatient() {
        this.existingNikoPatientId = null;
        this.activeNikoPatientDetailId = null;
        this.scenario = null;
        this.selectedExistingPatient = null;
        this.showExistingPatientList = false;
        this.advanceStep();
    }

    @action.bound
    async proceedWithExistingPatient() {
        if (!this.selectedExistingPatient || !this.existingNikoPatientId) return;
        this.showExistingPatientList = false;
        this.advanceStep();
    }

    @action.bound
    async advanceStep() {
        await this.stepperStore.goToNextStep();

        // If entering insurance step and existing patient, preload insurance
        if (this.isInsuranceInfoStep && this.existingNikoPatientId && !this.insurancePreloaded) {
            await this.preloadInsuranceData();
        }
    }

    // --- Step 2: Insurance Preloading ---

    @action.bound
    async preloadInsuranceData() {
        if (!this.activeNikoPatientDetailId) return;

        try {
            const response = await this.nikoPatientService.getExistingInsurance(this.activeNikoPatientDetailId);
            const data = response.data || response;

            runInAction(() => {
                this.existingInsuranceData = data;
                this.insurancePreloaded = true;

                // Prefill eligibility status dropdown
                if (data.eligibilityStatusId && data.eligibilityStatusName) {
                    this.insuranceStatusStore.setValue({ id: data.eligibilityStatusId, name: data.eligibilityStatusName });
                    this.form.$("insuranceStatusId").set("value", data.eligibilityStatusId);
                }

                // Prefill effective date
                if (data.effectiveDate) {
                    this.form.$("effectiveDate").set("value", data.effectiveDate);
                }

                // Preload notes
                if (data.insuranceInfoNote) {
                    this.form.$("insuranceInfoNote").set("value", data.insuranceInfoNote);
                    this._lastInsuranceInfoNote = data.insuranceInfoNote;
                }
                if (data.insuranceQuestion) {
                    this.form.$("insuranceQuestion").set("value", data.insuranceQuestion);
                }
                if (data.note) {
                    this.form.$("note").set("value", data.note);
                }
                if (data.eligibilityStatusNote) {
                    this.form.$("insuranceStatusNote").set("value", data.eligibilityStatusNote);
                }

                // Preload insurance policies
                if (data.insurances && data.insurances.length > 0) {
                    this.primaryPolicies = [];
                    this.secondaryPolicies = [];

                    data.insurances.forEach((ins) => {
                        const entry = this.createInsurancePolicyEntry(ins.active !== false, ins.primary);
                        entry.isEditing = false;
                        entry.backendId = ins.id || null;
                        entry.insuranceId = ins.insuranceId;
                        entry.insuranceName = ins.insuranceName || "";
                        entry.policyNo = ins.policyNo || "";
                        entry.startDate = ins.startDate || null;
                        entry.endDate = ins.endDate || null;
                        entry.relationship = ins.relationship || "Self";
                        entry.insuredFirstName = ins.insuredFirstName || "";
                        entry.insuredLastName = ins.insuredLastName || "";
                        entry.insuredDOB = ins.insuredDOB || null;
                        entry.insuredGender = ins.insuredGender || "";

                        if (ins.insuranceId) {
                            entry.insuranceStore.setValue({ id: ins.insuranceId, name: ins.insuranceName });
                        }
                        if (ins.relationship) {
                            entry.relationshipStore.setValue({ name: ins.relationship });
                        }
                        if (ins.insuredGender) {
                            entry.genderStore.setValue({ name: ins.insuredGender });
                        }

                        entry._snapshot = {
                            insuranceId: ins.insuranceId || null,
                            policyNo: ins.policyNo || "",
                            startDate: ins.startDate || null,
                            endDate: ins.endDate || null,
                            relationship: ins.relationship || "Self",
                            insuredFirstName: ins.insuredFirstName || "",
                            insuredLastName: ins.insuredLastName || "",
                            insuredDOB: ins.insuredDOB || null,
                            insuredGender: ins.insuredGender || "",
                            isActive: ins.active !== false,
                        };

                        if (ins.primary) {
                            this.primaryPolicies.push(entry);
                        } else {
                            this.secondaryPolicies.push(entry);
                        }
                    });
                }

            });
        } catch (err) {
            this.rootStore.notificationStore.error("Error preloading insurance data:", err && err.data);
        }
    }

    // --- Eligibility Status -> Note auto-fill ---

    @action.bound
    async computeAndSetEligibilityStatusNote(eligibilityStatusId) {
        const primaryActive = this.primaryPolicies.find(p => p.isActive);
        const primaryInsuranceName = primaryActive
            ? (primaryActive.insuranceStore.value ? primaryActive.insuranceStore.value.name : primaryActive.insuranceName) || null
            : null;
        const primaryPolicyNo = primaryActive ? (primaryActive.policyNo || null) : null;
        const insuranceInfoNote = this.form.$("insuranceInfoNote").value || null;

        try {
            const response = await this.nikoPatientDetailService.getEligibilityStatusNote({
                eligibilityStatusId,
                primaryInsuranceName,
                primaryPolicyNo,
                insuranceInfoNote,
            });
            const note = response.data != null ? response.data : "";
            runInAction(() => {
                this.form.$("insuranceStatusNote").set("value", note);
            });
        } catch (err) {
            this.rootStore.notificationStore.error("Error retrieving Eligibility Status Note.", err && err.data);
        }
    }

    @action.bound
    async onEligibilityStatusChange(newId) {
        await this.computeAndSetEligibilityStatusNote(newId);
    }

    // Recompute the ES Note when the primary active insurance changes, but only when the
    // currently selected Eligibility Status is "No OON Benefits" — for any other status,
    // changing insurance must not overwrite a manually-edited note. Mirrors the backend rule.
    @action.bound
    async onPrimaryInsuranceChange() {
        const esValue = this.insuranceStatusStore.value;
        const esName = esValue ? esValue.name : null;
        if (esName !== EligibilityStatusName.NoOonBenefits) return;
        const esId = esValue ? esValue.id : null;
        if (!esId) return;
        await this.computeAndSetEligibilityStatusNote(esId);
    }

    // Inline Insurance Info Note edits: store the new text immediately (so the field stays
    // responsive) and schedule a trailing recompute so we make one backend call after the user
    // pauses typing rather than one per keystroke.
    @action.bound
    setInsuranceInfoNote(value) {
        this.form.$("insuranceInfoNote").set("value", value);
        this.debouncedInfoNoteRecompute();
    }

    // Recompute the ES Note when the Insurance Info Note changes, but only when the currently
    // selected Eligibility Status is "Non-Billable Insurance". Mirrors the backend rule.
    @action.bound
    async onInsuranceInfoNoteChange() {
        const esValue = this.insuranceStatusStore.value;
        const esName = esValue ? esValue.name : null;
        if (esName !== EligibilityStatusName.NonBillableInsurance) return;
        const esId = esValue ? esValue.id : null;
        if (!esId) return;

        // Skip when the note text is unchanged since the last recompute, so a redundant trigger
        // neither fires a backend call nor clobbers a manual ES-note edit. Mirrors the backend's
        // isInsuranceInfoNoteChange guard.
        const infoNote = this.form.$("insuranceInfoNote").value || "";
        if (infoNote === this._lastInsuranceInfoNote) return;
        this._lastInsuranceInfoNote = infoNote;

        await this.computeAndSetEligibilityStatusNote(esId);
    }

    // --- Navigation ---

    @action.bound
    async goToNextStep() {
        if (this.isPatientInfoStep) {
            // Validate required fields
            this.form.validate();
            const fieldsValid = ["firstName", "lastName", "gender", "dateOfBirth"].every(
                (f) => this.form.$(f).isValid
            );
            if (!fieldsValid) {
                this.form.showErrors();
                return;
            }

            // Validate SSN: either SSN must have a value or "No SSN" must be checked
            if (!this.validateSsn()) return;

            // Check for existing patients
            if (!this.showExistingPatientList) {
                const matchCount = await this.checkForExistingPatients();
                if (matchCount > 0) {
                    // Show existing patient list, don't advance yet
                    return;
                }
                if (matchCount === -1) {
                    // Error occurred
                    return;
                }
            }

            // No matches or already handled — advance
            this.showExistingPatientList = false;
            this.advanceStep();
            return;
        }

        if (this.isTemplatesStep) {
            this.templateInstances = this.templateInstances.filter(inst => inst.store.subCategoryStore.value);
            if (this.pageType !== "II" && this.shouldShowParPopup()) {
                this.parConfirmed = false;
                this.showParPopup();
                return;
            }
            this.parConfirmed = false;
            this.advanceStep();
            return;
        }

        this.advanceStep();
    }

    @action.bound
    goToPreviousStep() {
        if (this.stepperStore.currentStep > 0) {
            this.stepperStore.currentStep -= 1;
        }
    }

    @action.bound
    goToStep(stepIndex) {
        if (!this.isSummaryStep) return;
        const stepName = this.getStepName(stepIndex);
        if (stepName === "patientInfo") return;
        this.stepperStore.currentStep = stepIndex;
    }

    @action.bound
    validateSsn() {
        const ssnValue = this.form.$("ssn").value;
        const noSsn = this.form.$("noSsn").value;
        this.ssnError = null;
        if (!ssnValue && !noSsn) {
            this.ssnError = "Please enter SSN or select No SSN.";
            return false;
        }
        if (ssnValue && ssnValue.length !== 9) {
            this.ssnError = "SSN must contain 9 digits.";
            return false;
        }
        return true;
    }

    @action.bound
    clearSsnError() {
        this.ssnError = null;
    }

    // --- Insurance Policy Management ---

    @action.bound
    createInsurancePolicyEntry(isActive, isPrimary) {
        const entry = observable.object({
            id: uuid(),
            isEditing: true,
            isEdit: false,
            errors: {},
            insuranceId: null,
            insuranceName: "",
            policyNo: "",
            startDate: null,
            endDate: null,
            endDateUnavailable: false,
            oldEndDate: null,
            oldEndDateUnavailable: false,
            relationship: "Self",
            insuredFirstName: "",
            insuredLastName: "",
            insuredDOB: null,
            insuredGender: "",
            isActive: isActive,
            isPrimary: isPrimary,
            insuranceStore: new BaasicDropdownStore(
                { filterable: true },
                {
                    onChange: () => {
                        if (this.policyErrors[entry.id] && this.policyErrors[entry.id].insuranceName) {
                            var newErrors = Object.assign({}, this.policyErrors[entry.id]);
                            delete newErrors.insuranceName;
                            this.policyErrors = Object.assign({}, this.policyErrors, { [entry.id]: newErrors });
                        }
                    },
                    fetchFunc: async (term) => {
                        const response = await this.insuranceService.find({
                            searchQuery: term,
                            isNiko: true,
                            isActive: true,
                            rpp: 2500,
                        });
                        const items = response.data.item;
                        if (!isActive && isPrimary) {
                            return items.filter(x => x.name === "Medicare");
                        }
                        if (!isActive && !isPrimary) {
                            return items.filter(x =>
                                x.name.toLowerCase().includes("medicaid") &&
                                !x.name.toLowerCase().includes("do not use") &&
                                !x.name.toLowerCase().includes("(medicaid")
                            );
                        }
                        return items;
                    },
                }
            ),
            relationshipStore: new BaasicDropdownStore(
                { textField: "name", dataItemKey: "name", filterable: true },
                {
                    onChange: () => {
                        runInAction(() => {
                            entry.insuredFirstName = "";
                            entry.insuredLastName = "";
                            entry.insuredDOB = null;
                            entry.insuredGender = "";
                            entry.genderStore.setValue(null);
                        });
                    },
                    fetchFunc: () => {
                        return [{ name: "Self" }, { name: "Spouse" }, { name: "Parent" }, { name: "Employer" }, { name: "Other" }];
                    },
                }
            ),
            genderStore: new BaasicDropdownStore(
                { textField: "name", dataItemKey: "name", filterable: true },
                {
                    fetchFunc: () => {
                        return [{ name: "Male" }, { name: "Female" }];
                    },
                }
            ),
        });
        entry.relationshipStore.setValue({ name: "Self" });
        return entry;
    }

    @action.bound
    addPrimaryPolicy() {
        this.primaryPolicies.push(this.createInsurancePolicyEntry(true, true));
    }

    @action.bound
    addInactivePrimaryPolicy() {
        this.primaryPolicies.push(this.createInsurancePolicyEntry(false, true));
    }

    @action.bound
    addSecondaryPolicy() {
        this.secondaryPolicies.push(this.createInsurancePolicyEntry(true, false));
    }

    @action.bound
    addInactiveSecondaryPolicy() {
        this.secondaryPolicies.push(this.createInsurancePolicyEntry(false, false));
    }

    @action.bound
    removePrimaryPolicy(index) {
        const policy = this.primaryPolicies[index];
        if (policy && policy.backendId) {
            this.removedPolicies.push(policy);
        }
        this.primaryPolicies.splice(index, 1);
        // Removing a primary policy can change which insurance is the primary active one.
        this.onPrimaryInsuranceChange();
    }

    @action.bound
    removeSecondaryPolicy(index) {
        const policy = this.secondaryPolicies[index];
        if (policy && policy.backendId) {
            this.removedPolicies.push(policy);
        }
        this.secondaryPolicies.splice(index, 1);
    }

    // CNP equivalent of the InsuranceChanged flag: a policy is "changed" only when its
    // insurance (policy name) differs from the value pulled in from the database, or when
    // the policy was newly added in the modal (no snapshot). Edits to dates/policy number on
    // an otherwise unchanged policy do NOT count as changed.
    isPolicyInsuranceChanged(policy) {
        if (!policy || !policy._snapshot) return true; // newly added (null -> A)
        const currentInsuranceId = policy.insuranceStore.value ? policy.insuranceStore.value.id : policy.insuranceId;
        return policy._snapshot.insuranceId !== currentInsuranceId;
    }

    // A policy is "swapped" when it had an original insurance (preloaded from the database, or
    // the value captured when the edit started) and the user has now selected a DIFFERENT one.
    // Plain edits (same insurance) and brand-new policies are NOT swaps. Drives the previous-insurance
    // End Date fields, which appear as soon as a different insurance is selected.
    isPolicySwapped(policy) {
        if (!policy) return false;
        const originalInsuranceId = policy._snapshot
            ? policy._snapshot.insuranceId
            : (policy._editBackup ? policy._editBackup.insuranceId : null);
        if (!originalInsuranceId) return false;
        const currentInsuranceId = policy.insuranceStore.value ? policy.insuranceStore.value.id : policy.insuranceId;
        return originalInsuranceId !== currentInsuranceId;
    }

    // Returns the action to expose on a present (not-null) policy: "delete" or "inactivate".
    // Before Send Order: changed policies can be deleted, unchanged policies are inactivated.
    // After Send Order: delete is never available, so always inactivate.
    getPolicyAction(policy) {
        if (this.isSendOrderActive) return "inactivate";
        return this.isPolicyInsuranceChanged(policy) ? "delete" : "inactivate";
    }

    @action.bound
    inactivatePolicy(type, index, endDate, endDateUnavailable) {
        const policies = type === "primary" ? this.primaryPolicies : this.secondaryPolicies;
        const policy = policies[index];
        if (!policy) return;

        // Record the termination date on the policy. Same outcome as Delete: the policy is
        // removed from the modal; the change is applied to the database only on final Confirm.
        // (Sending the termination date to Niko is out of scope for this task.)
        policy.endDate = endDateUnavailable ? null : endDate;
        policy.endDateUnavailable = !!endDateUnavailable;
        policy.inactivated = true;

        if (policy.backendId) {
            this.removedPolicies.push(policy);
        }
        policies.splice(index, 1);
        this.inactivatePolicyModal.close();

        // Inactivating a primary policy can change which insurance is the primary active one.
        if (type === "primary") {
            this.onPrimaryInsuranceChange();
        }
    }

    @action.bound
    updatePolicyField(type, index, field, value) {
        const policies = type === "primary" ? this.primaryPolicies : this.secondaryPolicies;
        if (policies[index]) {
            policies[index][field] = value;
            if (field === "endDateUnavailable" && value) {
                policies[index].endDate = null;
            }
            if (field === "oldEndDateUnavailable" && value) {
                policies[index].oldEndDate = null;
            }
            var policyId = policies[index].id;
            if (this.policyErrors[policyId] && (this.policyErrors[policyId][field] || (field === "endDateUnavailable" && this.policyErrors[policyId].endDate) || (field === "oldEndDateUnavailable" && this.policyErrors[policyId].oldEndDate))) {
                var newErrors = Object.assign({}, this.policyErrors[policyId]);
                delete newErrors[field];
                if (field === "endDateUnavailable") delete newErrors.endDate;
                if (field === "oldEndDateUnavailable") delete newErrors.oldEndDate;
                this.policyErrors = Object.assign({}, this.policyErrors, { [policyId]: newErrors });
            }
        }
    }

    @action.bound
    savePolicyEntry(type, index) {
        const policies = type === "primary" ? this.primaryPolicies : this.secondaryPolicies;
        const policy = policies[index];
        if (!policy) return;

        const errors = {};
        if (!policy.insuranceStore.value) {
            errors.insuranceName = "Insurance is required.";
        }
        if (!policy.policyNo) {
            errors.policyNo = "Policy number is required.";
        }
        if (!policy.startDate) {
            errors.startDate = "Start date is required.";
        }
        if (policy.startDate && policy.endDate && new Date(policy.endDate) <= new Date(policy.startDate)) {
            errors.endDate = "End Date must be greater than Start Date.";
        }
        // On swap, the previous insurance must be closed out: require its end date unless marked unavailable.
        if (this.isPolicySwapped(policy)) {
            if (!policy.oldEndDateUnavailable && !policy.oldEndDate) {
                errors.oldEndDate = "Enter an end date or check End Date Unavailable.";
            }
            else if (!policy.oldEndDateUnavailable && policy._snapshot && policy._snapshot.startDate && policy.oldEndDate && new Date(policy.oldEndDate) <= new Date(policy._snapshot.startDate)) {
                errors.oldEndDate = "End Date must be greater than Start Date.";
            }
        }

        const relationshipName = policy.relationshipStore.value ? policy.relationshipStore.value.name : "Self";
        const requiresInsured = relationshipName === "Spouse" || relationshipName === "Parent" || relationshipName === "Other";
        const requiresEmployer = relationshipName === "Employer";

        if (requiresInsured) {
            if (!policy.insuredFirstName) errors.insuredFirstName = "Insured First Name is required.";
            if (!policy.insuredLastName) errors.insuredLastName = "Insured Last Name is required.";
            if (!policy.insuredDOB) errors.insuredDOB = "Insured DOB is required.";
            else {
                const endOfToday = new Date();
                endOfToday.setHours(23, 59, 59, 999);
                if (new Date(policy.insuredDOB) > endOfToday) errors.insuredDOB = "Insured DOB cannot be in the future.";
            }
            if (!policy.genderStore.value) errors.insuredGender = "Insured Gender is required.";
        }
        if (requiresEmployer) {
            if (!policy.insuredLastName) errors.insuredLastName = "Employer Name is required.";
        }

        policy.errors = errors;
        this.policyErrors = Object.assign({}, this.policyErrors, { [policy.id]: errors });
        if (Object.keys(errors).length > 0) return;

        policy.insuranceId = policy.insuranceStore.value.id;
        policy.insuranceName = policy.insuranceStore.value.name;
        policy.relationship = relationshipName;
        policy.insuredGender = policy.genderStore.value ? policy.genderStore.value.name : "";
        policy._editBackup = null;
        policy.isEditing = false;
        policy.isEdit = false;

        // The primary active insurance is only finalized when the policy entry is committed, so
        // recompute the ES Note here — this captures both the insurance name and the policy number
        // (used for the "No OON Benefits" note when the insurance name is "Other").
        if (type === "primary") {
            this.onPrimaryInsuranceChange();
        }
    }

    @action.bound
    editPolicyEntry(type, index) {
        const policies = type === "primary" ? this.primaryPolicies : this.secondaryPolicies;
        const policy = policies[index];
        if (!policy) return;

        policy._editBackup = {
            insuranceId: policy.insuranceId,
            insuranceName: policy.insuranceName,
            policyNo: policy.policyNo,
            startDate: policy.startDate,
            endDate: policy.endDate,
            endDateUnavailable: policy.endDateUnavailable,
            oldEndDate: policy.oldEndDate,
            oldEndDateUnavailable: policy.oldEndDateUnavailable,
            relationship: policy.relationship,
            insuredFirstName: policy.insuredFirstName,
            insuredLastName: policy.insuredLastName,
            insuredDOB: policy.insuredDOB,
            insuredGender: policy.insuredGender,
            insuranceStoreValue: policy.insuranceStore.value,
            relationshipStoreValue: policy.relationshipStore.value,
            genderStoreValue: policy.genderStore.value,
        };
        policy.isEditing = true;
        policy.isEdit = true;
    }

    @action.bound
    cancelPolicyEntry(type, index) {
        const policies = type === "primary" ? this.primaryPolicies : this.secondaryPolicies;
        const policy = policies[index];
        if (!policy) return;

        if (policy._editBackup) {
            policy.insuranceId = policy._editBackup.insuranceId;
            policy.insuranceName = policy._editBackup.insuranceName;
            policy.policyNo = policy._editBackup.policyNo;
            policy.startDate = policy._editBackup.startDate;
            policy.endDate = policy._editBackup.endDate;
            policy.endDateUnavailable = policy._editBackup.endDateUnavailable;
            policy.oldEndDate = policy._editBackup.oldEndDate;
            policy.oldEndDateUnavailable = policy._editBackup.oldEndDateUnavailable;
            policy.relationship = policy._editBackup.relationship;
            policy.insuredFirstName = policy._editBackup.insuredFirstName;
            policy.insuredLastName = policy._editBackup.insuredLastName;
            policy.insuredDOB = policy._editBackup.insuredDOB;
            policy.insuredGender = policy._editBackup.insuredGender;
            policy.insuranceStore.setValue(policy._editBackup.insuranceStoreValue);
            policy.relationshipStore.setValue(policy._editBackup.relationshipStoreValue);
            policy.genderStore.setValue(policy._editBackup.genderStoreValue);
            policy._editBackup = null;
            policy.isEditing = false;
            policy.isEdit = false;
        } else {
            // New entry with no data — just remove it
            policies.splice(index, 1);
        }
    }

    // --- Helpers ---

    getPolicyType(isPrimary) {
        return isPrimary ? "Primary" : "Secondary";
    }

    isPolicyChanged(policy) {
        if (!policy._snapshot) return true; // new policy, not preloaded
        return !_.isEqual(policy._snapshot, {
            insuranceId: policy.insuranceStore.value ? policy.insuranceStore.value.id : null,
            policyNo: policy.policyNo || "",
            startDate: policy.startDate || null,
            endDate: policy.endDate || null,
            relationship: policy.relationshipStore.value ? policy.relationshipStore.value.name : "",
            insuredFirstName: policy.insuredFirstName || "",
            insuredLastName: policy.insuredLastName || "",
            insuredDOB: policy.insuredDOB || null,
            insuredGender: policy.insuredGender || "",
            isActive: policy.isActive,
        });
    }

    mapPolicyPayload(p, isPrimary) {
        const swapped = this.isPolicySwapped(p);
        return {
            type: this.getPolicyType(isPrimary),
            id: p.backendId || null,
            isNew: !p.backendId,
            isActive: p.isActive,
            insuranceId: p.insuranceId,
            policyNo: p.policyNo,
            startDate: p.startDate,
            endDate: p.endDate,
            oldEndDate: swapped ? (p.oldEndDateUnavailable ? null : p.oldEndDate) : null,
            oldEndDateUnavailable: swapped ? !!p.oldEndDateUnavailable : false,
            relationship: p.relationship,
            insuredFirstName: p.insuredFirstName || null,
            insuredLastName: p.insuredLastName || null,
            insuredDOB: p.insuredDOB || null,
            insuredGender: p.insuredGender || null,
            isChanged: this.isPolicyChanged(p),
        };
    }

    buildInsurancesPayload() {
        const insurances = [];
        this.primaryPolicies.forEach((p) => {
            if (p.insuranceId) {
                insurances.push(this.mapPolicyPayload(p, true));
            }
        });
        this.secondaryPolicies.forEach((p) => {
            if (p.insuranceId) {
                insurances.push(this.mapPolicyPayload(p, false));
            }
        });
        return insurances;
    }

    buildRemovedPoliciesPayload() {
        return this.removedPolicies
            .filter((p) => p.backendId)
            .map((p) => p.backendId);
    }

    buildTemplatesPayload() {
        const templates = [];
        const allTrachItems = [];

        const mapItem = (item) => ({
            productId: item.product ? item.product.id : item.productId,
            templateQuantity: item.quantity || 0,
            salesOrderQuantity: item.salesOrderQuantity || 0,
            note: item.note || null,
            itemCals: item.itemCals || 0,
            itemMls: item.itemMls || 0,
            mlph: item.mlph || 0,
            frequency: item.frequency || 0,
        });

        this.templateInstances.forEach((instance) => {
            const subCategoryId = instance.store.subCategoryStore.value
                ? instance.store.subCategoryStore.value.id
                : (instance.form.$("subCategoryId").value || null);

            if (!subCategoryId) return;

            const items = instance.items.filter((x) => x.product);
            const trachItems = instance.trachItems.filter((x) => x.product);

            templates.push({
                subCategoryId: subCategoryId,
                isUserDns: instance.store.isUserDns || false,
                isSystemDns: instance.store.isSystemDns || false,
                missingProductNote: instance.store.missingProductNote || "",
                note: instance.form.$("note").value || "",
                isPriorAuthNeeded: false,
                items: items.map(mapItem),
            });

            allTrachItems.push(...trachItems.map(mapItem));
        });

        return { templates, trachItems: allTrachItems };
    }

    getSummaryData() {
        const formValues = this.form.values();
        const patient = this.selectedExistingPatient;
        return {
            patientInfo: {
                firstName: patient ? patient.firstName : formValues.firstName,
                lastName: patient ? patient.lastName : formValues.lastName,
                nickname: patient ? patient.nickname : formValues.nickname,
                gender: patient ? patient.gender : formValues.gender,
                dateOfBirth: patient ? patient.dateOfBirth : formValues.dateOfBirth,
                ssn: patient ? (patient.ssn || "No SSN") : (formValues.noSsn ? "No SSN" : formValues.ssn),
                isExisting: !!this.existingNikoPatientId,
                existingPatient: patient,
                facility: this.facility
            },
            insuranceInfo: {
                insuranceStatus: this.insuranceStatusStore.value ? this.insuranceStatusStore.value.name : "",
                effectiveDate: formValues.effectiveDate,
                note: formValues.note,
                insuranceQuestion: formValues.insuranceQuestion,
                insuranceInfoNote: formValues.insuranceInfoNote,
                insuranceStatusNote: formValues.insuranceStatusNote,
                primaryPolicies: this.primaryPolicies.slice(),
                secondaryPolicies: this.secondaryPolicies.slice(),
            },
            templates: this.templateInstances.map((inst) => ({
                id: inst.id,
                subCategory: inst.store.subCategoryStore.value ? inst.store.subCategoryStore.value.name : null,
                note: inst.store.form.$('note').value || '',
                isUserDns: inst.store.isUserDns || false,
                isSystemDns: inst.store.isSystemDns || false,
                missingProductNote: inst.store.missingProductNote || '',
                items: inst.items.filter((x) => x.product),
                trachItems: inst.trachItems.filter((x) => x.product),
            })),
        };
    }

    // --- Step 4: Create Patient ---

    @action.bound
    async createResource() {
        this.disabled = true;

        try {
            // Make sure the ES Note reflects the final insurance / info-note edits before we read the
            // form — the CNP create path persists this note verbatim (no backend recompute on create).
            if (!this.isInsuranceStepSkipped) {
                this.debouncedInfoNoteRecompute.cancel();
                await this.onPrimaryInsuranceChange();
                await this.onInsuranceInfoNoteChange();
            }

            const formValues = this.form.values();
            const insuranceStatusValue = this.insuranceStatusStore.value;

            // Build templates with PAR flags if confirmed
            const buildResult = this.buildTemplatesPayload();
            const templates = this.parConfirmed
                ? this.templatesNeedingPar
                : buildResult.templates;
            const trachItems = buildResult.trachItems;

            const patient = this.selectedExistingPatient;
            const payload = {
                firstName: patient ? patient.firstName : formValues.firstName.trim(),
                lastName: patient ? patient.lastName : formValues.lastName.trim(),
                nickname: patient ? patient.nickname : formValues.nickname,
                gender: patient ? patient.gender : formValues.gender,
                dateOfBirth: patient ? patient.dateOfBirth : formValues.dateOfBirth,
                ssn: patient ? patient.ssn : (formValues.noSsn ? null : formValues.ssn),
                existingNikoPatientId: this.existingNikoPatientId || null,
                scenario: this.scenario || null,
                facilityId: this.facilityId,
                facilityOrderId: this.facilityOrderId,
                insurance: this.isInsuranceStepSkipped ? null : {
                    eligibilityStatus: insuranceStatusValue ? insuranceStatusValue.name : null,
                    eligibilityStatusId: insuranceStatusValue ? insuranceStatusValue.id : null,
                    eligibilityStatusEffectiveDate: formValues.effectiveDate || null,
                    note: formValues.note || null,
                    insuranceInfoNote: formValues.insuranceInfoNote || null,
                    insuranceQuestion: formValues.insuranceQuestion || null,
                    insuranceStatusNote: formValues.insuranceStatusNote || null,
                    insurances: this.buildInsurancesPayload(),
                    removedInsurances: this.buildRemovedPoliciesPayload(),
                },
                templates: templates,
                trachItems: trachItems,
                pageType: this.pageType,
            };

            const response = await this.nikoPatientService.createWithTemplates(payload);
            await this.onCreateSuccess();

            // ISG-871: surface drainage bag / leg bag validation warnings for auto-USI-reviewed templates created here.
            const validationMessages = response && response.data && response.data.customValues && response.data.customValues.validationMessages;
            if (validationMessages && validationMessages.length) {
                validationMessages.forEach((message) => this.rootStore.notificationStore.warning(message));
            }
        } catch (err) {
            runInAction(() => {
                this.disabled = false;
            });
            const errorMessage = (err && err.data && err.data.errors && err.data.errors.length > 0)
                ? err.data.errors[0]
                : "Error occurred while trying to create new patient.";
            this.rootStore.notificationStore.error(errorMessage);
        }
    }

    @action.bound
    async onCreateSuccess() {
        if (this.fetchPatientTemplates) {
            await this.fetchPatientTemplates();
        }

        runInAction(() => {
            this.disabled = false;
            this.parConfirmed = false;
        });
        this.modalParams.close();
        this.rootStore.notificationStore.success("Patient successfully created.");
    }

    // --- PAR Popup ---

    parConfirmed = false;

    shouldShowParPopup() {
        const insuranceStatusValue = this.insuranceStatusStore.value;
        if (!insuranceStatusValue || insuranceStatusValue.name !== "Eligible") return false;

        // Show PAR popup if primary active insurance is NOT Medicare
        const primaryActive = this.primaryPolicies.find((p) => p.isActive);
        if (!primaryActive || !primaryActive.insuranceName) return false;

        return !primaryActive.insuranceName.toLowerCase().includes("medicare");
    }

    @action.bound
    showParPopup() {
        const { templates } = this.buildTemplatesPayload();
        const subCategoryNames = {};
        this.templateInstances.forEach((instance) => {
            if (instance.store.subCategoryStore.value) {
                subCategoryNames[instance.store.subCategoryStore.value.id] = instance.store.subCategoryStore.value.name;
            }
        });
        const previousPar = this.templatesNeedingPar || [];
        this.templatesNeedingPar = templates.map((t, index) => ({
            ...t,
            subCategoryName: subCategoryNames[t.subCategoryId] || '',
            isPriorAuthNeeded: previousPar[index] ? previousPar[index].isPriorAuthNeeded : false,
        }));
        this.parConfirmModal.open();
    }

    @action.bound
    toggleParForTemplate(index) {
        if (this.templatesNeedingPar[index]) {
            this.templatesNeedingPar[index].isPriorAuthNeeded = !this.templatesNeedingPar[index].isPriorAuthNeeded;
        }
    }

    @action.bound
    confirmPar() {
        this.parConfirmModal.close();
        this.parConfirmed = true;
        this.advanceStep();
    }
}

export default NikoCreateNewPatientModalStore;
